import { REACT_APP_FRONTEND_URL } from 'env'
import { useEffect } from 'react'
import { sendMessageToRoot } from 'screens/TabRoot/utils'
import { useIsPwa } from 'utils/isPwa'
import { getOrigin } from 'utils/location'

function buildUrl(str: string): { url: string; sameOrigin: boolean } {
  // If str can be parsed as a URL, use str.
  try {
    const parsedUrl = new URL(str)
    return {
      url: str,
      sameOrigin: parsedUrl.origin === getOrigin() || parsedUrl.origin === REACT_APP_FRONTEND_URL,
    }
  } catch {}

  // If str cannot be parsed, use it as a relative path to our domain.
  try {
    const parsedUrl = new URL(str, getOrigin())
    return { url: parsedUrl.href, sameOrigin: parsedUrl.origin === getOrigin() }
  } catch {}

  // Neither worked. Fallback to str.
  return { url: str, sameOrigin: false }
}

// Intercepts clicks on anchor elements and opens them in a PWA tab.
export const usePwaGlobalLinkHandler = () => {
  const isPwa = useIsPwa()

  useEffect(() => {
    if (!isPwa) {
      return
    }

    const clickHandler = (ev: MouseEvent) => {
      if (ev.defaultPrevented) {
        return
      }
      const anchor = (ev.target as HTMLElement)?.closest('a')
      if (anchor == null) {
        return
      }
      if (anchor.target !== '_blank' && ev.button !== 1 /* mouse middle click */) {
        return
      }
      const { url, sameOrigin } = buildUrl(anchor.href)
      if (sameOrigin) {
        ev.preventDefault()
        sendMessageToRoot({ type: 'AddTab', url })
      }
      // Fall-through: we let the browser handle the link and open it as a browser tab.
    }

    window.addEventListener('click', clickHandler)
    window.addEventListener('auxclick', clickHandler)
    return () => {
      window.removeEventListener('click', clickHandler)
      window.removeEventListener('auxclick', clickHandler)
    }
  }, [isPwa])
}
